import React, { memo, useCallback } from "react";
import { Navigate, useMatch, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import { TStore } from "models";
import { sessionStorageKeys, useSessionStorage, isItAWhiteLabel } from "utils";

const stateSelectorHandle = createSelector(
	(state: TStore) => state.auth.credentials,
	(credentials) => ({ user: credentials }),
);

interface IGuesRoute {
	children: any; //TODO: fix the any
}

const GuestRoute: React.FunctionComponent<IGuesRoute> = ({ children }) => {
	const { setValue: setUgenieHubLink, storedValue: ugenieHubLink } =
		useSessionStorage({ key: sessionStorageKeys.ugenieHubLink });
	const stateSelector = useCallback(stateSelectorHandle, []);
	const { user } = useSelector(stateSelector);
	const location = useLocation();
	const [searchParams] = useSearchParams();
	// when the user is using a deep link he/she is redirected to the communities page
	const defaultPath =
		user && ugenieHubLink
			? `${!isItAWhiteLabel() ? "/communities" : "/joining/init"}`
			: "/dashboard";

	let matchDeepLink: any = useMatch({
		path: "/hubs/:token/sign-up",
	});

	// React Router v6: useMatch always returns an exact match or null — there is
	// no `isExact` property. Store the hub token whenever the path matches.
	if (matchDeepLink?.params?.token && !ugenieHubLink) {
		// set the hub's token in session storage
		setUgenieHubLink(matchDeepLink.params.token);
	}

	// Allow verify-email route for both authenticated and unauthenticated users
	if (location.pathname === "/verify-email") {
		return children;
	}

	// If the email link carried an invite token as a query param (e.g.
	// /auth/sign-in?invite_token=ABC or /auth/sign-up?invite_token=ABC),
	// forward unauthenticated visitors to the invite-acceptance screen so the
	// community data and branding are loaded before sign-up is shown.
	if (!user) {
		const inviteToken = searchParams.get("invite_token");
		const communityToken = searchParams.get("community_token");
		if (inviteToken) {
			return <Navigate to={`/invites/${inviteToken}`} replace />;
		}
		if (communityToken) {
			return <Navigate to={`/hubs/${communityToken}/sign-up`} replace />;
		}
	}

	return !user ? (
		children
	) : (
		<Navigate
			to={{
				pathname: defaultPath,
			}}
			state={{ from: location }}
		/>
	);
};

export default memo(GuestRoute);
